
import React, { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import SubscriptionCard from '@/components/subscription/SubscriptionCard';
import BillingToggle from '@/components/subscription/BillingToggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2, Mail, MessageCircle, Sparkles, Shield, Clock, Star, ArrowRight, Users } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plans, subscription, loading } = useSubscription();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF8C00]" />
      </div>
    );
  }

  // Get the single premium plan
  const premiumPlan = plans.find(plan => plan.name.toLowerCase() === 'premium') || plans[0];

  if (!premiumPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>No subscription plans available</p>
      </div>
    );
  }

  const isCurrentPlan = subscription?.subscription_tier === premiumPlan.name.toLowerCase() && 
                       subscription?.subscribed && 
                       new Date(subscription.subscription_end) > new Date();

  const handleGetStartedFree = () => {
    navigate('/auth');
  };

  return (
    <motion.div 
      className="min-h-screen bg-background"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Back Button */}
      <div className="container mx-auto px-4 pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 hover:bg-orange-50 transition-all duration-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Hero Section */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div 
              className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Sparkles className="h-4 w-4" />
              Premium Features
            </motion.div>
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Unlock Your <span className="text-primary">Potential</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Join thousands of ambitious teens already transforming their futures with TeenVisor Premium
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Features Overview Section */}
      <motion.div 
        className="container mx-auto px-4 py-16"
        variants={sectionVariants}
      >
        <motion.div 
          className="grid md:grid-cols-3 gap-6 mb-16"
          variants={containerVariants}
        >
          {[
            {
              icon: Shield,
              title: "Premium Support",
              description: "Priority access to mentors and career coaches",
              color: "orange"
            },
            {
              icon: Clock,
              title: "Early Access",
              description: "Get first access to new features and events",
              color: "blue"
            },
            {
              icon: Star,
              title: "Exclusive Content",
              description: "Access premium courses and resources",
              color: "purple"
            }
          ].map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={{
                hidden: { opacity: 0, y: 50, rotateY: -15 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  rotateY: 0,
                  transition: {
                    duration: 0.6,
                    delay: index * 0.15
                  }
                }
              }}
            >
              <Card className="text-center p-6 bg-white shadow-sm border transition-all duration-300 hover:shadow-md">
                <div className={`w-12 h-12 bg-${feature.color}-100 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                  <feature.icon className={`h-6 w-6 text-${feature.color}-600`} />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Billing Toggle */}
      <motion.div 
        className="container mx-auto px-4"
        variants={sectionVariants}
      >
        <div className="flex justify-center mb-12">
          <BillingToggle billingCycle={billingCycle} onToggle={setBillingCycle} />
        </div>
      </motion.div>

      {/* Pricing Plans Section */}
      <motion.div 
        className="container mx-auto px-4 pb-16"
        variants={sectionVariants}
      >
        <motion.div 
          className="grid lg:grid-cols-3 gap-8 items-start mb-16"
          variants={containerVariants}
        >
          {/* Free Plan */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, rotateY: -15 },
              visible: { 
                opacity: 1, 
                y: 0,
                rotateY: 0,
                transition: {
                  duration: 0.6,
                  delay: 0.1
                }
              }
            }}
          >
            <Card className="relative bg-white shadow-sm">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-xl mb-2">Free</CardTitle>
                <div className="text-3xl font-bold">$0</div>
                <p className="text-muted-foreground">Forever free</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Basic career resources</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Community access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Basic networking</span>
                  </div>
                </div>
                {user ? (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : (
                  <Button 
                    onClick={handleGetStartedFree}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    Get Started Free
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Premium Plan */}
          <motion.div 
            className="relative lg:scale-105"
            variants={{
              hidden: { opacity: 0, y: 50, rotateY: -15 },
              visible: { 
                opacity: 1, 
                y: 0,
                rotateY: 0,
                transition: {
                  duration: 0.6,
                  delay: 0.2
                }
              }
            }}
          >
            <SubscriptionCard
              plan={premiumPlan}
              billingCycle={billingCycle}
              isPopular={true}
              isCurrentPlan={isCurrentPlan}
            />
          </motion.div>

          {/* Enterprise Plan */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 50, rotateY: -15 },
              visible: { 
                opacity: 1, 
                y: 0,
                rotateY: 0,
                transition: {
                  duration: 0.6,
                  delay: 0.3
                }
              }
            }}
          >
            <Card className="relative bg-white shadow-sm">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-xl mb-2">Enterprise</CardTitle>
                <div className="text-3xl font-bold">Custom</div>
                <p className="text-muted-foreground">For organizations</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Everything in Premium</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Custom integrations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span>Dedicated support</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.location.href = 'mailto:teenvisor@gmail.com?subject=Enterprise Plan Inquiry'}
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Contact Information Section */}
      <motion.div 
        className="container mx-auto px-4 py-8"
        variants={sectionVariants}
      >
        <motion.div 
          className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 max-w-2xl mx-auto mb-8"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="flex items-center justify-center gap-2 mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Mail className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold">Ready to Get Started?</h3>
          </motion.div>
          <motion.p 
            className="text-muted-foreground mb-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Contact us to subscribe to TeenVisor Premium and unlock your potential
          </motion.p>
          <motion.div 
            className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <strong>How it works:</strong>
            <ol className="list-decimal list-inside mt-2 space-y-1 text-left">
              <li>Email us at teenvisor@gmail.com</li>
              <li>Include your preferred billing cycle</li>
              <li>Complete payment setup</li>
              <li>Start your premium journey</li>
            </ol>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="bg-muted/50 py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Transform Your Future Today
          </motion.h2>
          <motion.p 
            className="text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Every great achievement starts with the decision to try. 
            Join our community and get the support you need to build something amazing.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" className="bg-gradient-to-r from-primary to-primary/80" asChild>
                <Link to="/auth" className="flex items-center">
                  Start Your Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" variant="outline" className="flex items-center" asChild>
                <Link to="/auth">
                  <Users className="mr-2 h-4 w-4" />
                  Join Our Community
                </Link>
              </Button>
            </motion.div>
          </motion.div>
          <motion.p 
            className="mt-4 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            Already a member? <Link to="/auth" className="text-primary hover:underline">Sign in to upgrade</Link>
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Pricing;
